import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Sparkles,
  Check,
  History,
  Clapperboard,
  Lock,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from "lucide-react";

const WEEKDAYS = [
  "domenica",
  "lunedì",
  "martedì",
  "mercoledì",
  "giovedì",
  "venerdì",
  "sabato",
];

function prettyDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return `${WEEKDAYS[date.getDay()]} ${d}/${String(m).padStart(2, "0")}`;
}

export default function StoryIdeasCard({ isPro }) {
  const [loading, setLoading] = useState(false);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadToday = useCallback(async () => {
    if (!isPro) return;
    setLoading(true);
    try {
      const { data } = await api.get("/client/story-ideas/today");
      setToday(data.item);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  const loadHistory = useCallback(async () => {
    if (!isPro) return;
    try {
      const { data } = await api.get("/client/story-ideas", {
        params: { days: 8 },
      });
      const todayStr = today?.date;
      setHistory((data.items || []).filter((it) => it.date !== todayStr));
    } catch (e) {
      // silent
    }
  }, [isPro, today?.date]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, loadHistory]);

  const markDone = async (ideaId) => {
    if (!today?.date) return;
    try {
      const { data } = await api.post(
        `/client/story-ideas/${today.date}/${ideaId}/done`
      );
      setToday(data.item);
      toast.success("Segnata come fatta ✅");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!isPro) {
    return (
      <div
        data-testid="story-ideas-locked"
        className="rounded-3xl border border-[var(--mp-border)] bg-white p-6 sm:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-[var(--mp-yellow)] grid place-items-center">
            <Lock className="h-5 w-5 text-[var(--mp-ink)]" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
              Idee storie giornaliere · Pro
            </div>
            <div className="font-display text-2xl tracking-tight mt-1">
              Sblocca 3 idee AI ogni mattina
            </div>
            <p className="mt-2 text-[var(--mp-muted)] max-w-xl">
              Con il piano <strong>Pro</strong>, ogni giorno alle 9:00 ricevi su Telegram
              3 idee concrete di storie da pubblicare, calibrate sul tuo salone.
              Chiedi all'agenzia per attivarlo.
            </p>
            <Link
              to="/dashboard/chat"
              data-testid="story-ideas-locked-cta"
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--mp-ink)] text-white text-sm font-medium hover:bg-black"
            >
              <MessageSquare className="h-4 w-4" /> Contatta l'agenzia
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const ideas = today?.ideas || [];
  return (
    <div
      data-testid="story-ideas-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm overflow-hidden"
    >
      <div className="px-6 sm:px-8 py-5 border-b border-[var(--mp-border)] flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)] flex items-center gap-2">
            <Clapperboard className="h-3.5 w-3.5" /> Idee storie · Pro
          </div>
          <div className="font-display text-2xl tracking-tight mt-1">
            {today?.date
              ? `Oggi · ${prettyDate(today.date)}`
              : "Idee di oggi"}
          </div>
        </div>
        <Badge className="rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[10px] uppercase tracking-[0.2em]" title="Generate ogni notte da un modello AI (Claude/Gemini). Sono spunti — non repliche pronte da pubblicare. Adattale al tuo tono e alla tua giornata prima di usarle.">
          <Sparkles className="h-3 w-3 mr-1" /> Generate con AI
        </Badge>
      </div>

      <div className="p-6 sm:p-8 space-y-4">
        {loading && !today ? (
          <div className="text-center py-8 text-[var(--mp-muted)]">
            Sto generando le tue idee di oggi…
          </div>
        ) : ideas.length === 0 ? (
          <div className="text-center py-8 text-[var(--mp-muted)]">
            Nessuna idea disponibile al momento.
          </div>
        ) : (
          <div className="space-y-3">
            {ideas.map((idea, i) => (
              <IdeaRow
                key={idea.id}
                index={i + 1}
                idea={idea}
                onDone={() => markDone(idea.id)}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          data-testid="story-ideas-history-toggle"
          onClick={() => setShowHistory((v) => !v)}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 h-10 rounded-full border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] text-sm text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
        >
          <History className="h-4 w-4" />
          {showHistory ? "Nascondi storico" : "Mostra storico settimana"}
          {showHistory ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {showHistory && (
          <div
            data-testid="story-ideas-history"
            className="pt-4 border-t border-[var(--mp-border)] space-y-4"
          >
            {history.length === 0 ? (
              <div className="text-sm text-[var(--mp-muted)] text-center py-4">
                Nessuno storico precedente.
              </div>
            ) : (
              history.map((day) => (
                <div key={day.date} className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                    {prettyDate(day.date)}
                  </div>
                  <div className="space-y-2">
                    {(day.ideas || []).map((idea) => (
                      <div
                        key={idea.id}
                        className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3"
                      >
                        <div className="flex items-center gap-2">
                          {idea.done && (
                            <Check className="h-3.5 w-3.5 text-[var(--mp-ink)]" />
                          )}
                          <span className={`text-sm font-medium ${idea.done ? "line-through text-[var(--mp-muted)]" : ""}`}>
                            {idea.title}
                          </span>
                        </div>
                        {idea.description && (
                          <div className="text-xs text-[var(--mp-muted)] mt-1">
                            {idea.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function IdeaRow({ index, idea, onDone }) {
  return (
    <div
      data-testid={`story-idea-${idea.id}`}
      className={`rounded-2xl border ${
        idea.done
          ? "border-[var(--mp-border)] bg-[var(--mp-dash-bg)]"
          : "border-[var(--mp-ink)] bg-white"
      } p-4 sm:p-5`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`h-8 w-8 rounded-full grid place-items-center font-display text-sm shrink-0 ${
            idea.done
              ? "bg-[var(--mp-border)] text-[var(--mp-muted)]"
              : "bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
          }`}
        >
          {idea.done ? <Check className="h-4 w-4" /> : index}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`font-display text-lg tracking-tight leading-snug ${
              idea.done ? "line-through text-[var(--mp-muted)]" : ""
            }`}
          >
            {idea.title}
          </div>
          {idea.description && (
            <div
              className={`mt-1.5 text-sm leading-relaxed ${
                idea.done ? "text-[var(--mp-muted)]" : "text-[var(--mp-ink)]/80"
              }`}
            >
              {idea.description}
            </div>
          )}
          <div className="mt-3">
            {!idea.done ? (
              <Button
                size="sm"
                data-testid={`story-idea-done-${idea.id}`}
                onClick={onDone}
                variant="outline"
                className="rounded-full border-[var(--mp-ink)] h-8 text-xs"
              >
                <Check className="h-3.5 w-3.5 mr-1" /> Segna come fatta
              </Button>
            ) : (
              <Badge className="rounded-full bg-[var(--mp-ink)] text-white text-[10px] uppercase tracking-[0.2em]">
                Fatta
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
